import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import { toDownlineResult } from '@/api/map/downline'
import type { AgentNode } from '@/api/map/agent'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

async function withBalances(nodes: AgentNode[]): Promise<AgentNode[]> {
  return Promise.all(
    nodes.map(async (node) => {
      try {
        return await apiFetch<AgentNode>(`/v1/agents/${node.id}`)
      } catch {
        return node
      }
    }),
  )
}

/** Live downline = hierarchy Postgres tree + TigerBeetle balances only. */
function emptyDownline(query: api.DownlineQuery): api.DownlineResult {
  return {
    rows: [],
    total: 0,
    page: query.page ?? 1,
    pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    totals: {
      creditLimit: 0,
      balance: 0,
      exposure: 0,
      toCollect: 0,
      toPay: 0,
      members: 0,
      agents: 0,
    },
  }
}

async function listDownlineLive(query: api.DownlineQuery): Promise<api.DownlineResult> {
  const rootId = query.rootId ?? getCallerAgentId()
  if (!rootId) return emptyDownline(query)

  const tree = await apiFetch<AgentNode[]>(`/v1/agents/${rootId}/tree`)
  const nodes = await withBalances(tree)
  return toDownlineResult(nodes, rootId, query)
}

async function listDownline(query: api.DownlineQuery): Promise<api.DownlineResult> {
  if (hasLiveApi()) {
    try {
      return await listDownlineLive(query)
    } catch (err) {
      if (isUnreachableApiError(err)) return emptyDownline(query)
      throw err
    }
  }
  return api.listDownline(query)
}

export function useDownline(query: api.DownlineQuery) {
  return useQuery({
    queryKey: keys.downline(query),
    queryFn: () => listDownline(query),
    placeholderData: (previous) => previous,
  })
}
